/**
 * Per-route SEO resolvers used by route `head()` functions.
 *
 * Every title/description/canonical/schema here is read from the SAME data
 * modules the page components render from, so the server HTML and the hydrated
 * DOM describe the same page. Schema builders in src/lib/seo.ts are reused
 * as-is; no shapes are changed here.
 */
import { COMPANY, breadcrumbSchema, faqSchema, serviceSchema } from "@/lib/seo";
import { buildHead, type RouteHeadInput } from "@/lib/route-head";
import { homepageSchemas, hubSchemas, locationSchemas } from "@/data/route-schemas";
import { pageMeta } from "@/data/page-meta";
import {
  allElectricalServices,
  hvacServices,
  energyServices,
  industryServices,
  stadiumServices,
  type ServiceData,
} from "@/data/services";
import { allLocations } from "@/data/locations";
import { blogPosts, getBlogPost } from "@/data/blog-posts";
import { guides } from "@/data/guides";
import { guideFaqs } from "@/data/guide-faqs";
import { faqs as siteFaqs } from "@/pages/FAQ";

const allServices: ServiceData[] = [
  ...allElectricalServices,
  ...hvacServices,
  ...energyServices,
  ...industryServices,
  ...stadiumServices,
];

const meta = (path: string) => pageMeta[path];

const notFoundHead = (title: string, description: string) =>
  buildHead({ title, description, canonical: null, noIndex: true });

/* ── Static pages ── */

export function staticHead(
  path: string,
  overrides: Partial<RouteHeadInput> & { schemas?: (object | null | undefined)[] } = {}
) {
  const m = meta(path);
  return buildHead({
    title: overrides.title ?? m?.title ?? COMPANY.name,
    description: overrides.description ?? m?.description ?? "",
    canonical: path,
    ...(overrides.noIndex !== undefined ? { noIndex: overrides.noIndex } : {}),
    ...(overrides.follow !== undefined ? { follow: overrides.follow } : {}),
    schemas: overrides.schemas ?? [],
  });
}

const crumbs = (items: { name: string; url: string }[]) => [breadcrumbSchema(items)];

export const homeHead = () => staticHead("/", { schemas: homepageSchemas() });

export const aboutHead = () =>
  staticHead("/about", { schemas: crumbs([{ name: "Home", url: "/" }, { name: "About", url: "/about" }]) });

export const safetyHead = () =>
  staticHead("/about/safety", {
    schemas: crumbs([
      { name: "Home", url: "/" },
      { name: "About", url: "/about" },
      { name: "Safety", url: "/about/safety" },
    ]),
  });

export const faqPageHead = (faqs: { question: string; answer: string }[] = siteFaqs) =>
  staticHead("/about/faq", {
    schemas: [
      faqSchema(faqs),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About", url: "/about" },
        { name: "FAQ", url: "/about/faq" },
      ]),
    ],
  });

export const careersHead = () =>
  staticHead("/about/careers", {
    schemas: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About", url: "/about" },
        { name: "Careers", url: "/about/careers" },
      ]),
    ],
  });

export const portfolioHead = () =>
  staticHead("/portfolio", {
    schemas: crumbs([{ name: "Home", url: "/" }, { name: "Portfolio", url: "/portfolio" }]),
  });

export const reviewsHead = () =>
  staticHead("/reviews", { schemas: crumbs([{ name: "Home", url: "/" }, { name: "Reviews", url: "/reviews" }]) });

export const contactHead = () =>
  staticHead("/contact", { schemas: crumbs([{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }]) });

export const requestEstimateHead = () =>
  buildHead({
    title: "Free Estimate | Hadley Energy",
    description:
      "Request a free estimate for electrical, HVAC or energy services in Louisville, KY. Same-day response guaranteed. (502) 921-3777.",
    canonical: "/request-estimate",
    schemas: crumbs([{ name: "Home", url: "/" }, { name: "Request Estimate", url: "/request-estimate" }]),
  });

export const privacyHead = () =>
  staticHead("/privacy-policy", {
    schemas: crumbs([{ name: "Home", url: "/" }, { name: "Privacy Policy", url: "/privacy-policy" }]),
  });

export const sitemapHead = () =>
  staticHead("/sitemap", {
    noIndex: true,
    follow: true,
    schemas: crumbs([{ name: "Home", url: "/" }, { name: "Sitemap", url: "/sitemap" }]),
  });

/* ── Blog index ── */

export const blogIndexHead = () =>
  staticHead("/blog", {
    schemas: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Blog & Resources",
        description:
          "Expert advice on electrical safety, energy savings, and smart building solutions for Louisville, KY and Southern Indiana businesses and homeowners.",
        url: `${COMPANY.url}/blog`,
        inLanguage: "en-US",
        publisher: {
          "@type": "Organization",
          name: COMPANY.name,
          logo: { "@type": "ImageObject", url: `${COMPANY.url}/images/hadley-logo.png`, width: 1428, height: 883 },
        },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: blogPosts.map((post, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: post.title,
            url: `${COMPANY.url}/blog/${post.slug}`,
          })),
        },
      },
    ],
  });

/* ── Hub overviews ── */

const HUB_TITLES: Record<string, { title: string; description: string }> = {
  "/electrical-services": {
    title: "Commercial Electrical Services in Louisville, KY",
    description:
      "Licensed electrical contractor in Louisville, KY. Browse every electrical service we offer: commercial, industrial, service and repair, and 24/7 emergency.",
  },
  "/hvac-services": {
    title: "Commercial & Residential HVAC Services in Louisville, KY",
    description:
      "From installations to repairs and maintenance, our HVAC experts deliver dependable heating and cooling solutions - Hadley Energy Solutions",
  },
  "/energy-management": {
    title: "Energy Management Louisville KY | ESCO & Efficiency",
    description:
      "Energy management and efficiency programs for Louisville, KY facilities: energy audits, guaranteed savings, lighting controls and utility bill analysis.",
  },
  "/industries": {
    title: "Industrial Electrical & HVAC Contractor Louisville KY",
    description:
      "Industrial electrical & HVAC contractor in Louisville, KY for manufacturing, healthcare, schools, stadiums & data centers. (502) 921-3777.",
  },
  "/stadium-sports-lighting": {
    title: "Stadium Lighting Installation Louisville KY | LED Sports",
    description:
      "Stadium lighting installation & LED sports lighting in Louisville, KY. Fields, courts, arenas & retrofits. Licensed CE64623. (502) 921-3777.",
  },
  "/service-area": {
    title: "Electrician & HVAC Service Areas | Louisville KY",
    description:
      "Licensed electrician & HVAC contractor serving Louisville, KY, Lexington, Shepherdsville & Southern Indiana. (502) 921-3777.",
  },
};

export function hubHead(path: keyof typeof HUB_TITLES | string) {
  const copy = HUB_TITLES[path];
  const builder = hubSchemas[path];
  return buildHead({
    title: copy?.title ?? meta(path)?.title ?? COMPANY.name,
    description: copy?.description ?? meta(path)?.description ?? "",
    canonical: path,
    schemas: builder ? builder() : [],
  });
}

/* ── Dynamic service pages ── */

export function serviceHead(basePath: string, baseLabel: string, slug: string) {
  const service = allServices.find((s) => s.slug === slug);
  if (!service) return notFoundHead("Service Not Found", "This service page could not be found.");

  return buildHead({
    title: service.metaTitle,
    description: service.metaDescription,
    canonical: `${basePath}/${service.slug}`,
    schemas: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: baseLabel, url: basePath },
        { name: service.title, url: `${basePath}/${service.slug}` },
      ]),
      faqSchema(service.faqs),
      serviceSchema({
        name: service.title,
        description: service.metaDescription,
        url: `${basePath}/${service.slug}`,
        category: baseLabel,
      }),
    ],
  });
}

/* ── City pages ── */

export function locationHead(slug: string) {
  const location = allLocations.find((l) => l.slug === slug);
  if (!location) return notFoundHead("Location Not Found", "This service area page could not be found.");

  return buildHead({
    title: location.metaTitle,
    description: location.metaDescription,
    canonical: `/service-area/${location.slug}`,
    schemas: locationSchemas(location),
  });
}

/* ── Blog posts ── */

export function blogPostHead(slug: string) {
  const post = getBlogPost(slug);
  if (!post) return notFoundHead("Article Not Found", "This article could not be found.");

  return buildHead({
    title: post.metaTitle,
    description: post.metaDescription,
    canonical: `/blog/${post.slug}`,
    schemas: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${post.slug}` },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title.length > 110 ? post.title.slice(0, 110) : post.title,
        author: { "@type": "Organization", name: COMPANY.name, url: COMPANY.url },
        publisher: {
          "@type": "Organization",
          name: COMPANY.name,
          logo: { "@type": "ImageObject", url: `${COMPANY.url}/images/hadley-logo.png`, width: 1428, height: 883 },
        },
        datePublished: post.publishedDate,
        dateModified: post.modifiedDate,
        image: `${COMPANY.url}/images/og-image.jpg`,
        description: post.metaDescription,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${COMPANY.url}/blog/${post.slug}` },
        wordCount: post.content.reduce(
          (acc, s) => acc + (s.content?.split(/\s+/).length || 0) + (s.items?.join(" ").split(/\s+/).length || 0),
          0
        ),
        inLanguage: "en-US",
      },
      faqSchema(post.faqs),
    ],
  });
}

/* ── Guides ── */

export function guideHead(slug: string) {
  const guide = guides.find((g) => g.slug === slug);
  if (!guide) return notFoundHead("Guide Not Found", "This guide could not be found.");

  return buildHead({
    title: guide.metaTitle,
    description: guide.metaDescription,
    canonical: `/guide/${guide.slug}`,
    schemas: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Guides", url: "/blog" },
        { name: guide.title, url: `/guide/${guide.slug}` },
      ]),
      ...(guideFaqs[guide.slug] ? [faqSchema(guideFaqs[guide.slug]!)].filter(Boolean) : []),
      serviceSchema({
        name: guide.title,
        description: guide.metaDescription,
        url: `/guide/${guide.slug}`,
        category: "Guide",
      }),
      ...guide.sections
        .filter((s) => s.items && s.items.length > 0)
        .map((section) => ({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${section.heading} — ${guide.title}`,
          description: section.content,
          url: `/guide/${guide.slug}#${section.heading
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")}`,
          numberOfItems: section.items!.length,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          itemListElement: section.items!.map((item, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: item,
          })),
        })),
    ],
  });
}

/* ── Admin (always noindex) ── */

export const adminHead = (title: string, description: string, canonical: string) =>
  buildHead({ title, description, canonical, noIndex: true });

/* ── Slug validators for 404 handling ──
   Used by dynamic route beforeLoad guards: an unknown slug throws notFound()
   so the server responds with a real HTTP 404 instead of a soft-404 at 200. */

export const isServiceSlug = (slug: string) => allServices.some((s) => s.slug === slug);
export const isLocationSlug = (slug: string) => allLocations.some((l) => l.slug === slug);
export const isBlogSlug = (slug: string) => !!getBlogPost(slug);
export const isGuideSlug = (slug: string) => guides.some((g) => g.slug === slug);
